import React, { useEffect, useMemo } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";

interface RunHistoryFilters {
  status: string;
}

interface RunHistoryFilterProps {
  status: string;
  statusOptions: { value: string; label: string }[];
  onApplyFilters: (filters: RunHistoryFilters) => void;
  onResetFilters: () => void;
}

export const RunHistoryFilter: React.FC<RunHistoryFilterProps> = ({
  status,
  statusOptions,
  onApplyFilters,
  onResetFilters,
}) => {
  // Transform statusOptions to options format
  const statusSelectOptions = useMemo(
    () =>
      statusOptions?.map((status) => ({
        value: status.value,
        label: status.label,
      })) || [],
    [statusOptions]
  );

  // Define filter fields
  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "status",
        label: "Status",
        type: "select",
        placeholder: "Select Status",
        options: statusSelectOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
    ],
    [statusSelectOptions]
  );

  // Create form instance
  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Sync form values when URL params change
  useEffect(() => {
    form.reset({
      status: decodeURIComponent(status) || "",
    });
  }, [status, form]);

  const handleFilterSubmit = () => {
    const formValues = form.getValues();
    onApplyFilters({
      status: formValues.status || "",
    });
  };

  const handleFilterReset = () => {
    form.reset({
      status: "",
    });
    onResetFilters();
  };

  const handleFieldChange = (field: string, value: string) => {
    // Add any field-specific logic here if needed
  };

  return (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterReset}
        onFieldChange={handleFieldChange}
        gridColumns={1}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply Filters",
          resetButtonText: "Clear",
          classes: {
            buttonContainer: "flex justify-end gap-4",
          },
        }}
      />
    </div>
  );
};
